import {
  ReactNode, useEffect, useMemo, useState,
} from 'react';
import {
  Button,
  Flex,
  InfoList,
  isUserWithRoles,
  ToastStatus,
  useAsyncFunctionProgress,
  useCombinedUser,
  UserRoles,
  useToastNotifications,
  WarningInfo,
  WarningInfoDanger,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import { formatDateTime } from '@equifinance/shared/utils';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { ApiCredentials } from '../../../../contexts/apiCredentials';
import { PaddedSection } from '../../../Account/components/Section';
import useBrokerManagementApi from '../../../../api/useBrokerManagementApi';

const APISectionCss = css` ${({ theme }) => `
  background-color: ${theme.colours.primary[8]};
`}`;

const CopyIcon = styled.i` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};  
  font-size: ${theme.fontSize[3]};
  cursor: pointer;
`}`;

const DataRow = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[5]};
`}`;

const DataBox = styled(Flex)` ${({ theme }) => `
  flex: 1;
  align-items: center;
  justify-content: space-between;
  background: ${theme.colours.neutral[10]};
  border-radius: ${theme.borderRadius[1]};
  padding: ${theme.padding[1]} ${theme.padding[6]};
`}`;

const Label = styled.p` ${({ theme }) => ` 
  color:${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[7]};
`}`;

const RightSide = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[4]};
  align-items: center;
`}`;

const Value = styled(Label)` 
  text-align: right;
`;

const GenerateTokenContainer = styled(Flex)` ${({ theme }) => ` 
  flex: 1;
  align-items: center;
  justify-content: end;
  padding-left: calc(${theme.padding[3]} + ${theme.padding[9]});
`}`;

const ErrorContainer = styled.div`${({ theme }) => `
  margin-top: -${theme.padding[4]};
  border-radius: ${theme.borderRadius[2]};
  box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
`}`;

function Data({ label, value, suffix = <></> }: { label: string, value: any; suffix?: ReactNode }) {
  return (
    <DataBox>
      <Label>{label}</Label>
      <RightSide>
        <Value>{value}</Value>
        {suffix}
      </RightSide>
    </DataBox>
  );
}

export const ApiManagement = 'api-management';

export default function ApiManagementSection() {
  const { id: companyId } = useParams() as { id: string };
  const [apiCredentials, setApiCredentials] = useState<ApiCredentials>({});
  const { getApiCredentials, postApiCredentials } = useBrokerManagementApi();

  const { clientId, secretValidTo, clientSecret } = apiCredentials;
  const [isRegenerated, setIsRegenerated] = useState(false);

  const { addToastNotification } = useToastNotifications();

  const showToastMessage = (text: string, type: ToastStatus) => {
    addToastNotification({
      text,
      type,
      showCloseIcon: true,
    });
  };

  const { user } = useCombinedUser();

  const canGenerateToken = isUserWithRoles([UserRoles.CAN_REGENERATE_API_SECRET, UserRoles.BROKER_ADMIN], user);

  const secretInValid = secretValidTo && moment(secretValidTo).isBefore(moment());

  const warningMessage = useMemo(() => {
    let message = `Secret is hidden and it is not possible to retrieve the secret from the system.
    If you have lost your secret you will need to generate a new one using the button below`;

    if (!clientSecret) {
      message = 'Secret hasn’t been generated previously. To generate an secret please use the button below';
    }

    if (clientSecret && isRegenerated) message = 'Secret will be hidden on page re-load, please copy the secret now and store safely';

    if (secretInValid) message = 'Secret is no longer valid, please re-generate secret';

    return message;
  }, [clientSecret, isRegenerated, secretInValid]);

  const fetchApiCredentials = async (companyId: string) => {
    const data = await getApiCredentials(companyId);
    setApiCredentials(data);
    return data;
  };

  const generateApiCredentials = async (companyId: string) => {
    const data = await postApiCredentials(companyId);
    setApiCredentials(data);
    return data;
  };

  useEffect(() => {
    if (!companyId) return;
    fetchApiCredentials(companyId);
  }, [companyId]);

  const onGenerateApiCredentials = async () => {
    if (!canGenerateToken) return;

    try {
      await generateApiCredentials(companyId);
      setIsRegenerated(true);
      showToastMessage('Token successfully refreshed, please take note of your new secret as leaving this page will mask your new secret', ToastStatus.SUCCESS);
    } catch {
      setIsRegenerated(false);
      showToastMessage('Unable to refresh token. If the issue persists please contact the Sales Support Team for assistance.', ToastStatus.WARNING);
    }
  };

  const { inProgress, execute: executeGenerateApiCredentials } = useAsyncFunctionProgress(onGenerateApiCredentials);

  const copyToClipboard = (text?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
  };

  const WarningBox = secretInValid ? WarningInfoDanger : WarningInfo;

  return (
    <PaddedSection
      id={ApiManagement}
      refName={ApiManagement}
      title="API"
      bodyCss={APISectionCss}
      titleIcon={<></>}
    >
      {warningMessage && (
        <ErrorContainer>
          <WarningBox>
            <InfoList items={[warningMessage]} />
          </WarningBox>
        </ErrorContainer>
      )}
      <DataRow>
        <Data
          label="Client ID"
          suffix={<CopyIcon className="mi-copy" onClick={() => { copyToClipboard(clientId); }} />}
          value={clientId ?? ''}
        />
        <Data
          label="Client Secret"
          suffix={(isRegenerated && clientSecret) && <CopyIcon className="mi-copy" onClick={() => { copyToClipboard(clientSecret); }} />}
          value={clientSecret ?? 'Not set'}
        />
      </DataRow>

      <DataRow>
        <Data
          label="Secret valid until"
          value={secretValidTo ? formatDateTime(secretValidTo, 'DD/MM/YYYY') : '-'}
        />
        <GenerateTokenContainer>
          {canGenerateToken && <Button small onClick={executeGenerateApiCredentials} disabled={inProgress}>{!clientSecret ? 'Generate Secret' : 'Re-generate Secret'}</Button>}
        </GenerateTokenContainer>
      </DataRow>
    </PaddedSection>
  );
}
